'use client';

import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/src/hooks/use-api-client';
import { AssessmentResults } from './assessment-results';
import type { SpeechAssessmentResponse } from '@/src/types/assessment';
import { Button } from '@/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/src/components/ui/dialog';
import { Volume2 } from 'lucide-react';

interface RecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  script: string;
  audioUrl?: string;
}

export const RecordingModal = ({
  isOpen,
  onClose,
  script,
  audioUrl
}: RecordingModalProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [assessment, setAssessment] = useState<SpeechAssessmentResponse | null>(
    null
  );
  const audioChunks = useRef<Blob[]>([]);
  const apiClient = useApiClient();

  // Cleanup function for media resources
  const cleanupMediaResources = () => {
    if (mediaRecorder) {
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      setMediaRecorder(null);
    }

    if (audioStream) {
      audioStream.getTracks().forEach((track) => {
        track.stop();
      });
      setAudioStream(null);
    }

    setIsRecording(false);
    audioChunks.current = [];
  };

  // Cleanup on unmount or modal close
  useEffect(() => {
    if (!isOpen) {
      cleanupMediaResources();
    } else {
      // Clear past results when modal is opened
      setAssessment(null);
    }
    return () => {
      cleanupMediaResources();
    };
  }, [isOpen]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      const recorder = new MediaRecorder(stream);
      audioChunks.current = [];

      recorder.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        submitRecording(audioBlob);
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    console.log('recorder state ', mediaRecorder?.state);
    if (mediaRecorder?.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      cleanupMediaResources();
    }
  };

  const submitRecording = async (audioBlob: Blob) => {
    try {
      setIsLoading(true);
      const result = await apiClient.submitSpeechAssessment(audioBlob, script);
      setAssessment(result as SpeechAssessmentResponse);
    } catch (error) {
      console.error('Error submitting recording:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    cleanupMediaResources();
    setAssessment(null);
    onClose();
  };

  // Function to play the audio from the provided URL
  const playAudio = () => {
    if (audioUrl) {
      new Audio(audioUrl).play();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Record Your Pronunciation</DialogTitle>
        </DialogHeader>

        <div className='mt-2'>
          <p className='mb-4 text-base text-gray-500'>
            Please read the following text:
          </p>
          <div className='mb-6 flex items-center justify-between rounded-lg bg-gray-50 p-4'>
            <p className='text-lg font-medium'>{script}</p>
            {audioUrl && (
              <Button
                variant='outline'
                size='sm'
                className='ml-2 shrink-0 text-blue-600'
                onClick={playAudio}
              >
                <Volume2 size={18} className='mr-1' />
                Listen
              </Button>
            )}
          </div>

          {isLoading && (
            <div className='mb-6 space-y-2'>
              <div className='h-1 w-full overflow-hidden rounded bg-gray-200'>
                <div className='h-full animate-progress bg-blue-500'></div>
              </div>
              <p className='text-center text-base text-gray-500'>
                Analyzing your pronunciation...
              </p>
            </div>
          )}

          {assessment && <AssessmentResults assessment={assessment} />}
        </div>

        <DialogFooter className='gap-2'>
          <Button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading}
            variant={isRecording ? 'destructive' : 'default'}
            className='w-full'
          >
            {isLoading
              ? 'Processing...'
              : isRecording
                ? 'Stop Recording'
                : 'Start Recording'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
